import { EventBookingStatus, EventSystemStatus, ICalendlyEvent, ICore, IImportData, IPersonalInfo, IRecordedInterview, IRecordedInterviewAttemptEntry, IScheduledEvent, RecordedInterviewSystemStatus, IMessage } from 'api-paths-cas';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Button } from '@chakra-ui/react';
import { Fragment, useEffect, useState } from 'react';
import { hasPerm, P, isEnrolyStaff, isAgentRole } from '../../../../utilities/roles/permissions';
import { UserRole } from '../../../../store/auth/types';
import FingerWithApprove from '../../../atoms/finger-with-approve';
import { Section } from '../../../Finger/Section';
import { hashToArray, isRecordedInterviewEmpty } from '../../../../utilities/helpers';
import {
  getFingerStatus,
  getFormattedDate,
  getFormattedDateRange,
  getInterviewAccess,
  getRecordedInterviewFingerStatus,
} from '../../../../utilities/scheduledInterviews';
import { BookingDetailsSection, CalendlyWidget } from 'shared-components';
import { sortFn } from '../../../../utilities/documents';
import RequestNewInterview from '../../modals/requestNewInterview';
import { IEventType, IEventTypesState } from '../../../../store/event_types/types';
import { SectionId } from '../../../molecules/studentLeftNav/types';
import CancelInterview from '../../modals/cancelInterview';
import CancelRecordedInterview from '../../modals/cancelRecordedInterview';
import InterviewAction from '../../modals/interviewAction';
import { FileUploadModal } from '../../modals/fileUpload';
import { IModal } from '../../../../store/ui/types';
import { ApplicationState } from '../../../../store';
import { useDispatch, useSelector } from 'react-redux';
import { useIsSectionLocked } from '../../../../hooks/useIsSectionLocked';
import LockedSectionWarning from '../../../molecules/locked-section-warning';
import CASBypassWarning from '../../../molecules/casBypassWarning/casBypassWarning';
import deepEqual from 'fast-deep-equal/es6/react';
import {
  failRecordedInterview,
  passRecordedInterview,
  resitRecordedInterview,
} from '../../../../store/student/actions';
import RecordedInterviewModal from '../../modals/watchRecordedInterviewModal';
import { decrypt } from '../../../../utilities/crypto';
import InterviewAssessmentResults from './components/InterviewAssessmentResults';
import { DocumentRequestButtons } from '../../../DocumentRequestButtons';
import { SectionGroup } from '../../../Finger/SectionGroup';
import { SummaryRow } from '../../../Summary/SummaryRow';
import { DocumentViewer } from 'shared-components';

const isPDF = (urlString: string): boolean => {
  const url = new URL(urlString);
  return url.pathname.endsWith('.pdf');
};

export interface IProps {
  calendlyEvents?: Array<ICalendlyEvent>;
  personalInfo?: IPersonalInfo;
  core?: ICore;
  importData?: IImportData;
  userRole: UserRole;
  eventTypes: IEventTypesState;
  uploadModal: IModal | null;
  isScheduledInterviewsHidden: boolean;
  isRecordedInterviewHidden: boolean;
}

export interface IDispatchProps {
  getUniEventTypes: () => void;
  getUniRegionInterviewConfig: (uniId: string, pathway?: string) => void;
  onSubmitMessage: (message: IMessage) => void;
  openUploadDocumentModal: (
    interviewType: string,
    eventType: string,
    eventKey: string,
    modalTitle: string,
    fileNames: string[],
  ) => void;
  openUploadRecordedInterviewModal: (
    interviewType: string,
    interviewHash: string,
    attemptHash: string,
    modalTitle: string,
    fileNames: string[],
  ) => void;
  closeUploadDocumentModal: () => void;
  onCancelRecordedInterview: (interviewHash: string, note: string) => void;
  onRequestRecordedInterview: (interviewId: string, note: string) => void;
  onRequestInterview: (eventType: IEventType, note: string) => void;
  onCancelInterview: (eventTypeId: string, eventKey: string, note: string) => void;
  onFailInterview: (
    eventTypeId: string,
    eventName: string,
    eventKey: string,
    note: string,
    rescind?: boolean,
    sourceComponentId?: string,
  ) => void;
  onResitInterview: (
    eventTypeId: string,
    eventName: string,
    eventKey: string,
    note: string,
    rescind?: boolean,
    newCalendlyInterview?: IEventType,
    sourceComponentId?: string,
  ) => void;
  onPassInterview: (
    eventTypeId: string,
    eventName: string,
    eventKey: string,
    status?: EventSystemStatus,
    sourceComponentId?: string,
  ) => void;
  onGetRecordedInterviews: () => void;
  onGetCalendlyEvents: () => void;
}

export interface ScheduledInterviewList {
  key: string;
  item: IScheduledEvent;
}

enum ModalType {
  BOOK_INTERVIEW = 'BOOK_INTERVIEW',
  REQUEST_NEW_INTERVIEW = 'REQUEST_NEW_INTERVIEW',
  CANCEL_INTERVIEW = 'CANCEL_INTERVIEW',
  FAIL_INTERVIEW = 'FAIL_INTERVIEW',
  RESCIND_FAIL_INTERVIEW = 'RESCIND_FAIL_INTERVIEW',
  RESIT_INTERVIEW = 'RESIT_INTERVIEW',
  RESCIND_RESIT_INTERVIEW = 'RESCIND_RESIT_INTERVIEW',
  OPEN_INTERVIEW_PREVIEW = 'OPEN_INTERVIEW_PREVIEW',
  CANCEL_RECORDED_INTERVIEW = 'CANCEL_RECORDED_INTERVIEW',
}

interface ScheduledInterviewModal {
  type?: ModalType;
  interview?: ICalendlyEvent;
  event?: IScheduledEvent;
  eventKey?: string;
  interviewHash?: string;
  attemptHash?: string;
  signedUrls?: string[];
  interviewName?: string;
  sourceComponentId?: string;
  currentInterviewId?: string;
}
interface IRecordedInterviewAttemptEntryExtended extends IRecordedInterviewAttemptEntry {
  attemptHash: string;
}

type Props = IProps & IDispatchProps;

const ScheduledInterviews = (props: Props) => {
  const {
    eventTypes,
    calendlyEvents,
    personalInfo,
    importData,
    core,
    userRole,
    getUniEventTypes,
    getUniRegionInterviewConfig,
    isScheduledInterviewsHidden,
    isRecordedInterviewHidden,
    onGetRecordedInterviews,
    onGetCalendlyEvents,
  } = props;

  const serviceProviderType = useSelector((state: ApplicationState) => state.auth.serviceProviderType);

  const isAdmin = isEnrolyStaff(userRole);
  const isAgent = isAgentRole(userRole);
  const isAgencyAdmin = userRole === UserRole.AgencyAdmin;
  const isPartner = userRole === UserRole.Partner;

  const firstName = `${personalInfo?.givenName ?? importData?.givenName ?? ''} ${
    personalInfo?.middleName ?? ''
  }`.trim();
  const lastName = personalInfo?.surname ?? importData?.familyName ?? '';
  const studentDetails = {
    name: `${firstName ?? ''} ${lastName ?? ''}`.trim(),
    email: core?.email ?? '',
    firstName,
    lastName,
  };

  const {
    offer,
    uniId,
    studentNumber,
    isAddAndCancelInterviewVisible,
    pathway,
    isPathwayStudent,
    isPathwayProvider,
    isCasBypass,
    regionInterviewConfig,
    recordedInterviews,
  } = useSelector(({ student, config, auth }: ApplicationState) => {
    return {
      isPathwayProvider: auth.isPathway ?? false,
      offer: student.offer,
      uniId: student.core.university,
      studentNumber: student.core.student_number,
      isAddAndCancelInterviewVisible: config.relatedPathwaySettings?.addAndCancelInterviewVisible ?? false,
      pathway: student.core.pathway,
      isPathwayStudent: !!student.core.pathway,
      isCasBypass: student.offer.casBypass,
      regionInterviewConfig: config.regionInterviewConfig,
      recordedInterviews: (student.offer as any).recordedInterviews || null,
    };
  }, deepEqual);

  const isSectionLocked = useIsSectionLocked(SectionId.SCHEDULED_INTERVIEWS, offer.status);

  const [isCanceling, setIsCanceling] = useState(false);
  const [openModal, setOpenModal] = useState<ScheduledInterviewModal | null>(null);

  const [watchInterviewModalHash, setWatchInterviewModalHash] = useState('');

  const canRequest = hasPerm(userRole, P.SCHEDULED_INTERVIEWS_REQUEST);
  const canCancel = hasPerm(userRole, P.SCHEDULED_INTERVIEWS_CANCEL);

  const canAttach = hasPerm(userRole, P.SCHEDULED_INTERVIEWS_ATTACH);
  const canOpen = hasPerm(userRole, P.SCHEDULED_INTERVIEWS_PREVIEW, serviceProviderType);
  const canPass = hasPerm(userRole, P.SCHEDULED_INTERVIEWS_PASS);
  const canResit = hasPerm(userRole, P.SCHEDULED_INTERVIEWS_RESIT);
  const canFail = hasPerm(userRole, P.SCHEDULED_INTERVIEWS_FAIL);

  const isScheduledInterviewsVisible = !isScheduledInterviewsHidden && hasPerm(userRole, P.SCHEDULED_INTERVIEWS_VIEW);

  const isRecordedInterviewVisible =
    !isRecordedInterviewHidden && hasPerm(userRole, P.RECORDED_INTERVIEW_VIEW, serviceProviderType);

  const isInterviewAssessor = isPartner && serviceProviderType === 'IA';
  const canWatchRecordedInterview = !isAgent && !isAgencyAdmin && (!isPartner || isInterviewAssessor);

  const dispatch = useDispatch();

  const canRequestInterview = getInterviewAccess({
    userRole,
    hasRoleAccess: canRequest,
    isPathwayStudent,
    isPathwayProvider,
    isAddAndCancelInterviewVisible,
  });
  const canCancelInterview = getInterviewAccess({
    userRole,
    hasRoleAccess: canCancel,
    isPathwayStudent,
    isPathwayProvider,
    isAddAndCancelInterviewVisible,
  });

  useEffect(() => {
    if (canRequest) {
      getUniEventTypes();
    }
    getUniRegionInterviewConfig(uniId || '', pathway);
  }, [getUniEventTypes, canRequest, getUniRegionInterviewConfig, uniId, pathway]);

  useEffect(() => {
    onGetRecordedInterviews();
    onGetCalendlyEvents();
  }, []);

  const renderCalendlyEvents = () => {
    return calendlyEvents!.map((interview: ICalendlyEvent, calendlyIndex: number) => {
      const scheduledInterviews = hashToArray(interview.scheduledEvents ?? {}).sort(sortFn);
      if (
        scheduledInterviews?.length &&
        !scheduledInterviews.some(
          (doc: ScheduledInterviewList) => doc?.item?.systemStatus !== EventSystemStatus.DELETED,
        )
      ) {
        return null;
      }
      return (
        <Section
          id={`event-type-${interview.eventType}`}
          key={`event-type-${interview.eventType}-${calendlyIndex}`}
          label={interview.name}
          canEdit={false}
        >
          {scheduledInterviews.map((doc: ScheduledInterviewList, scheduledInterviewIndex: number) => {
            const { key, item } = doc;

            const hasDocs = (item?.fileNames ?? []).length > 0;
            if (item.systemStatus === EventSystemStatus.DELETED && !hasDocs) {
              return null;
            }

            const getLabel = () => {
              const date = getFormattedDateRange(item.startDate, item.endDate);
              if (item.bookingStatus === EventBookingStatus.REQUESTED) {
                return 'Requested';
              }
              if (item.bookingStatus === EventBookingStatus.BOOKED) {
                return `Booked (${date})`;
              }
              if (item.bookingStatus === EventBookingStatus.CANCELED) {
                return `Cancelled (${date})`;
              }
              return 'Unknown';
            };

            const getLabelBtn = () => {
              if (item.bookingStatus === EventBookingStatus.REQUESTED) {
                return (
                  <Button
                    data-selector={`${interview.eventType}-${key}-action-book`}
                    onClick={() => setOpenModal({ type: ModalType.BOOK_INTERVIEW, interview, event: item })}
                  >
                    Book
                  </Button>
                );
              }
            };

            return (
              <FingerWithApprove
                id={`${interview.eventType}-${key}`}
                key={`${interview.eventType}-${key}-${scheduledInterviewIndex}`}
                status={getFingerStatus(item)}
                isProcessingPdf={item.isProcessingPdf}
                fileCount={item.signedUrls && item.signedUrls.length > 0 ? item.signedUrls.length : 0}
                fileNames={item.fileNames}
                label={getLabel()}
                labelBtn={getLabelBtn()}
                canAttach={canAttach}
                canOpen={canOpen}
                canPass={canPass}
                canResit={canResit}
                canFail={canFail}
                isDeleting={isCanceling}
                onAttachClick={() => {
                  props.openUploadDocumentModal(
                    'calendlyInterviews',
                    interview.eventType,
                    key,
                    'Interview Document',
                    item.fileNames ?? [],
                  );
                }}
                onOpenClick={() => {
                  const signedUrls = item?.signedUrls ?? [];
                  const pdfFiles = signedUrls.filter(file => isPDF(file));
                  if (pdfFiles.length) {
                    window.open(`${pdfFiles[0]}`);
                  } else {
                    setOpenModal({
                      type: ModalType.OPEN_INTERVIEW_PREVIEW,
                      signedUrls: item?.signedUrls ?? [],
                    });
                  }
                }}
                onPassClick={() =>
                  props.onPassInterview(
                    interview.eventType,
                    interview.name,
                    key,
                    item.systemStatus !== EventSystemStatus.PASSED ? EventSystemStatus.PASSED : undefined,
                    `${interview.eventType}-${key}`,
                  )
                }
                onResitClick={() =>
                  item.systemStatus === EventSystemStatus.RESIT
                    ? setOpenModal({
                        type: ModalType.RESCIND_RESIT_INTERVIEW,
                        interview,
                        event: item,
                        eventKey: key,
                        sourceComponentId: `${interview.eventType}-${key}`,
                      })
                    : setOpenModal({
                        type: ModalType.RESIT_INTERVIEW,
                        interview,
                        event: item,
                        eventKey: key,
                        sourceComponentId: `${interview.eventType}-${key}`,
                      })
                }
                onFailClick={() =>
                  item.systemStatus === EventSystemStatus.FAILED
                    ? setOpenModal({
                        type: ModalType.RESCIND_FAIL_INTERVIEW,
                        interview,
                        event: item,
                        eventKey: key,
                        sourceComponentId: `${interview.eventType}-${key}`,
                      })
                    : setOpenModal({
                        type: ModalType.FAIL_INTERVIEW,
                        interview,
                        event: item,
                        eventKey: key,
                        sourceComponentId: `${interview.eventType}-${key}`,
                      })
                }
                onDeleteClick={() =>
                  setOpenModal({
                    type: ModalType.CANCEL_INTERVIEW,
                    interview,
                    event: item,
                    eventKey: key,
                    sourceComponentId: `${interview.eventType}-${key}`,
                  })
                }
                userRole={props.userRole}
              >
                <>
                  {item.note && <SummaryRow label="Interview request notes" value={item.note} />}
                  {item.actionReason && <SummaryRow label="Notes" value={item.actionReason} />}
                  <BookingDetailsSection interview={interview} event={item} />
                </>
              </FingerWithApprove>
            );
          })}
        </Section>
      );
    });
  };

  const renderRecordedInterviews = (recordedInterviews: IRecordedInterview) => {
    if (recordedInterviews) {
      const sortedRecordedInterviews = Object.entries(recordedInterviews)
        .map(item => {
          const [interviewHash, interview] = item;
          return {
            ...interview,
            interviewHash,
          };
        })
        .sort((a, b) => (a.hashTimestamp ?? 1) - (b.hashTimestamp ?? 0));

      return sortedRecordedInterviews.map(interview => {
        let attempt: IRecordedInterviewAttemptEntryExtended;
        if (
          interview.systemStatus === RecordedInterviewSystemStatus.DELETED ||
          interview.systemStatus === RecordedInterviewSystemStatus.EXCEEDED
        ) {
          return null;
        }
        const isInvited = interview.systemStatus === RecordedInterviewSystemStatus.INVITED;
        // if status INVITED, there is no attempts yet, but we still want to show the finger indicating the student is invited
        if (isInvited) {
          attempt = {
            attemptHash: 'Invited',
            timestamp: 0,
            recorded: false,
          };
        } else {
          const sortedAttempts = Object.entries(interview.attempts)
            .map(([attemptHash, attempt]) => {
              return {
                ...attempt,
                attemptHash,
              };
            })
            .sort((a, b) => a.timestamp - b.timestamp);
          attempt = sortedAttempts[sortedAttempts.length - 1];
        }

        const dateFormatted = getFormattedDate(attempt.timestamp);
        const label = isInvited
          ? 'Invited'
          : attempt.recorded
            ? `Recorded (${dateFormatted})`
            : `Attempted (${dateFormatted})`;

        const encryptedQuestions = Object.values(interview.attempts).find(
          attempt => attempt.recorded === true,
          // @ts-ignore
        )?.questions;
        let attemptQuestions;
        if (typeof encryptedQuestions === 'string') {
          const decryptQuestions = decrypt(encryptedQuestions as string);
          attemptQuestions = JSON.parse(decryptQuestions!);
        } else {
          attemptQuestions = encryptedQuestions;
        }

        const { userAssessment, note, actionReason } = interview;
        const isAssessmentRevealed = userAssessment?.revealAssessmentToUni;
        const hasOutcomeData = !!note || !!actionReason;
        const canSeeDropdown = !isInvited && canWatchRecordedInterview;

        const shouldShowDropdownContent = canSeeDropdown && (isAssessmentRevealed || hasOutcomeData);

        return (
          <Section
            id={`event-type-${interview.interviewHash}`}
            key={`event-type-${interview.interviewHash}`}
            label={interview.interviewName}
            canEdit={false}
          >
            <Fragment key={`${attempt.attemptHash}`}>
              <FingerWithApprove
                id={`${attempt.attemptHash}`}
                key={`${attempt.attemptHash}`}
                status={getRecordedInterviewFingerStatus(interview, attempt.recorded, isAgent || isAgencyAdmin)}
                isProcessingPdf={attempt.isProcessingPdf}
                fileCount={attempt.signedUrls?.length ? attempt.signedUrls.length : 0}
                fileNames={attempt.fileNames}
                label={label}
                labelBtn={
                  attempt.recorded && canWatchRecordedInterview ? (
                    <Button
                      as={RouterLink}
                      to={`/student/${uniId}/${studentNumber}/${offer.intakeId}/${offer.offerId}/interviews/${interview.interviewHash}`}
                    >
                      Watch
                    </Button>
                  ) : undefined
                }
                canAttach={canAttach && !isInvited}
                canOpen={canOpen && !isInvited}
                canPass={canPass && attempt.recorded}
                canResit={canResit && attempt.recorded}
                canFail={canFail && attempt.recorded}
                isDeleting={isCanceling}
                onAttachClick={() => {
                  props.openUploadRecordedInterviewModal(
                    'recordedInterviews',
                    interview.interviewHash,
                    attempt.attemptHash,
                    'Interview Document',
                    attempt.fileNames ?? [],
                  );
                }}
                onOpenClick={() => {
                  setOpenModal({
                    type: ModalType.OPEN_INTERVIEW_PREVIEW,
                    signedUrls: attempt.signedUrls,
                  });
                }}
                onPassClick={() => {
                  dispatch(
                    passRecordedInterview(
                      interview.interviewHash,
                      interview.systemStatus !== RecordedInterviewSystemStatus.PASSED
                        ? RecordedInterviewSystemStatus.PASSED
                        : RecordedInterviewSystemStatus.RECORDED,
                      `${attempt.attemptHash}`,
                    ),
                  );
                }}
                onResitClick={() => {
                  if (interview.systemStatus === RecordedInterviewSystemStatus.RESIT) {
                    setOpenModal({
                      type: ModalType.RESCIND_RESIT_INTERVIEW,
                      interviewHash: interview.interviewHash,
                      interviewName: interview.interviewName,
                      sourceComponentId: `${attempt.attemptHash}`,
                    });
                  } else {
                    setOpenModal({
                      type: ModalType.RESIT_INTERVIEW,
                      interviewHash: interview.interviewHash,
                      interviewName: interview.interviewName,
                      currentInterviewId: interview.interviewId,
                      sourceComponentId: `${attempt.attemptHash}`,
                    });
                  }
                }}
                onFailClick={() => {
                  if (interview.systemStatus === RecordedInterviewSystemStatus.FAILED) {
                    setOpenModal({
                      type: ModalType.RESCIND_FAIL_INTERVIEW,
                      interviewHash: interview.interviewHash,
                      interviewName: interview.interviewName,
                      sourceComponentId: `${attempt.attemptHash}`,
                    });
                  } else {
                    setOpenModal({
                      type: ModalType.FAIL_INTERVIEW,
                      interviewHash: interview.interviewHash,
                      interviewName: interview.interviewName,
                      sourceComponentId: `${attempt.attemptHash}`,
                    });
                  }
                }}
                onDeleteClick={() => {
                  setOpenModal({
                    type: ModalType.CANCEL_RECORDED_INTERVIEW,
                    interviewHash: interview.interviewHash,
                    interviewName: interview.interviewName,
                  });
                }}
                userRole={props.userRole}
                deletedByTTL={interview.deletedByTTL}
              >
                {shouldShowDropdownContent ? (
                  <>
                    {hasOutcomeData ? (
                      <>
                        {interview.note && <SummaryRow label="Interview request notes" value={interview.note} />}

                        {interview.actionReason && <SummaryRow label="Notes" value={interview.actionReason} />}
                      </>
                    ) : undefined}

                    {isAssessmentRevealed ? (
                      <InterviewAssessmentResults interview={interview} isAdmin={isAdmin} />
                    ) : undefined}
                  </>
                ) : undefined}
              </FingerWithApprove>
              {watchInterviewModalHash === attempt.attemptHash && (
                <RecordedInterviewModal
                  questions={
                    attemptQuestions.length
                      ? attemptQuestions
                      : typeof interview.questions === 'string'
                        ? JSON.parse(decrypt(interview.questions!)!)
                        : interview.questions
                  }
                  hashTimestamp={interview.hashTimestamp ?? 0}
                  hash={interview.hash ?? ''}
                  videoId={interview.videoId ?? ''}
                  onClose={() => setWatchInterviewModalHash('')}
                />
              )}
            </Fragment>
          </Section>
        );
      });
    }
    return null;
  };

  switch (openModal?.type) {
    case ModalType.OPEN_INTERVIEW_PREVIEW: {
      const imageArray = openModal.signedUrls;
      return imageArray && imageArray.length > 0 ? (
        <DocumentViewer documents={imageArray} onClose={() => setOpenModal(null)} />
      ) : null;
    }
    case ModalType.BOOK_INTERVIEW: {
      return (
        <CalendlyWidget
          url={
            openModal?.interview?.schedulingUrl
              ? `${openModal.interview.schedulingUrl}?utm_content=${openModal.interview.utmKey ?? ''}`
              : ''
          }
          prefill={studentDetails}
          onClose={() => setOpenModal(null)}
        />
      );
    }
    case ModalType.REQUEST_NEW_INTERVIEW: {
      return (
        <RequestNewInterview
          eventTypes={eventTypes}
          onSendMessage={(message: IMessage) => props.onSubmitMessage(message)}
          onRequestInterview={(eventType: IEventType, note: string) => props.onRequestInterview(eventType, note)}
          onRequestRecordedInterview={(interviewId: string, note: string) =>
            props.onRequestRecordedInterview(interviewId, note)
          }
          regionInterviewConfig={regionInterviewConfig}
          onClose={() => setOpenModal(null)}
        />
      );
    }
    case ModalType.CANCEL_INTERVIEW: {
      return (
        <CancelInterview
          interview={openModal?.interview}
          event={openModal?.event}
          eventKey={openModal?.eventKey}
          studentDetails={studentDetails}
          userRole={userRole}
          onSendMessage={(message: IMessage) => props.onSubmitMessage(message)}
          onCancelRequest={(eventType: string, eventKey: string, note: string) =>
            props.onCancelInterview(eventType, eventKey, note)
          }
          onClose={() => setOpenModal(null)}
        />
      );
    }
    case ModalType.CANCEL_RECORDED_INTERVIEW: {
      return (
        <CancelRecordedInterview
          interviewName={openModal?.interviewName}
          userRole={userRole}
          onSendMessage={(message: IMessage) => props.onSubmitMessage(message)}
          onCancelRequest={(note: string) => {
            if (openModal.interviewHash) props.onCancelRecordedInterview(openModal.interviewHash, note);
          }}
          onClose={() => setOpenModal(null)}
        />
      );
    }
    case ModalType.FAIL_INTERVIEW: {
      return (
        <InterviewAction
          id={'fail-interview'}
          interviewState={'failed'}
          interviewName={openModal?.interviewName}
          interview={openModal?.interview}
          userRole={userRole}
          onSendMessage={(message: IMessage) => props.onSubmitMessage(message)}
          onSubmit={(note: string) => {
            // recorded interviews
            if (openModal.interviewHash) {
              dispatch(failRecordedInterview(openModal.interviewHash, note, false, openModal.sourceComponentId));
            } else {
              // calendly interviews
              props.onFailInterview(
                openModal?.interview?.eventType ?? '',
                openModal?.interview?.name ?? '',
                openModal?.eventKey ?? '',
                note,
                false,
                openModal?.sourceComponentId,
              );
            }
          }}
          onClose={() => setOpenModal(null)}
        />
      );
    }
    case ModalType.RESCIND_FAIL_INTERVIEW: {
      return (
        <InterviewAction
          id={'rescind-fail-interview'}
          interviewState={'failed'}
          interviewName={openModal?.interviewName}
          interview={openModal?.interview}
          userRole={userRole}
          onSendMessage={(message: IMessage) => props.onSubmitMessage(message)}
          onSubmit={(note: string) => {
            if (openModal.interviewHash) {
              dispatch(failRecordedInterview(openModal.interviewHash, note, true, openModal.sourceComponentId));
            } else {
              props.onFailInterview(
                openModal?.interview?.eventType ?? '',
                openModal?.interview?.name ?? '',
                openModal?.eventKey ?? '',
                note,
                true,
                openModal?.sourceComponentId,
              );
            }
          }}
          onClose={() => setOpenModal(null)}
        />
      );
    }
    case ModalType.RESIT_INTERVIEW: {
      return (
        <InterviewAction
          id={'resit-interview'}
          interviewState={'resit requested'}
          interview={openModal?.interview}
          interviewName={openModal?.interviewName}
          regionInterviewConfig={regionInterviewConfig}
          eventTypes={eventTypes}
          userRole={userRole}
          currentInterviewId={openModal?.currentInterviewId}
          onSendMessage={(message: IMessage) => props.onSubmitMessage(message)}
          onSubmit={(note: string, newInterviewId?: string) => {
            let newRecordedInterviewId;
            let newCalendlyInterview;
            let newInterviewName;
            if (newInterviewId) {
              newCalendlyInterview = eventTypes.find(event => event.id === newInterviewId);
              newRecordedInterviewId = newInterviewId;

              const selectedInterview = regionInterviewConfig?.find(
                interview => interview.interviewId === newInterviewId,
              );
              newInterviewName = selectedInterview?.interviewName;
            }
            // recorded interviews
            if (openModal.interviewHash) {
              dispatch(
                resitRecordedInterview(
                  openModal.interviewHash,
                  note,
                  undefined,
                  newRecordedInterviewId,
                  newInterviewName,
                  openModal?.sourceComponentId,
                ),
              );
            } else {
              // calendly interviews
              props.onResitInterview(
                openModal?.interview?.eventType ?? '',
                openModal?.interview?.name ?? '',
                openModal?.eventKey ?? '',
                note,
                undefined,
                newCalendlyInterview,
                openModal?.sourceComponentId,
              );
            }
          }}
          onClose={() => setOpenModal(null)}
        />
      );
    }
    case ModalType.RESCIND_RESIT_INTERVIEW: {
      return (
        <InterviewAction
          id={'rescind-resit-interview'}
          interviewState={'resit requested'}
          interview={openModal?.interview}
          interviewName={openModal?.interviewName}
          userRole={userRole}
          onSendMessage={(message: IMessage) => props.onSubmitMessage(message)}
          onSubmit={(note: string) => {
            // recorded interviews
            if (openModal.interviewHash) {
              dispatch(
                resitRecordedInterview(openModal.interviewHash, note, true, undefined, openModal.sourceComponentId),
              );
            } else {
              // calendly interviews
              props.onResitInterview(
                openModal?.interview?.eventType ?? '',
                openModal?.interview?.name ?? '',
                openModal?.eventKey ?? '',
                note,
                true,
                undefined,
                openModal?.sourceComponentId,
              );
            }
          }}
          onClose={() => setOpenModal(null)}
        />
      );
    }
    default: {
      if (props.uploadModal?.isUpload) {
        return <FileUploadModal {...props.uploadModal} />;
      }
      return (
        <Box>
          {isCasBypass ? (
            <CASBypassWarning />
          ) : (
            <>
              {isRecordedInterviewVisible && isRecordedInterviewEmpty(recordedInterviews) && (
                <SectionGroup
                  heading="Recorded video interviews"
                  description={
                    (isAgent || isAgencyAdmin) && (
                      <>
                        The student needs to complete a short video recording answering some questions. The student will
                        need to log in to CAS Shield to complete this task at a time that is convenient for them. Please
                        advise the student that they will need to have a good internet connection, a working camera and
                        a microphone, as well as have their passport ready before starting.
                      </>
                    )
                  }
                >
                  {isSectionLocked && <LockedSectionWarning />}

                  {renderRecordedInterviews(recordedInterviews!)}
                </SectionGroup>
              )}

              {isScheduledInterviewsVisible && calendlyEvents?.length ? (
                <SectionGroup
                  heading="Interview bookings"
                  description={
                    (isAgent || isAgencyAdmin) && (
                      <>
                        The student needs to attend an interview with a member of our team. You can schedule a time for
                        the interview on behalf of your student. Once scheduled, will see the booking details here.
                      </>
                    )
                  }
                >
                  {isSectionLocked && <LockedSectionWarning />}

                  {renderCalendlyEvents()}
                </SectionGroup>
              ) : (
                <></>
              )}

              <DocumentRequestButtons
                canRequest={canRequestInterview}
                canDelete={canCancelInterview}
                isDeleting={isCanceling}
                requestLabel="Request new interview"
                deleteLabel="Cancel interview request"
                onRequestClick={() => setOpenModal({ type: ModalType.REQUEST_NEW_INTERVIEW })}
                onDeleteClick={() => setIsCanceling(!isCanceling)}
              />
            </>
          )}
        </Box>
      );
    }
  }
};

export default ScheduledInterviews;
